import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@coreshop/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@coreshop/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';
import { storefrontCustomerGuard } from '@coreshop/feature-storefront/guards/storefront-customer.guard';
import { storefrontTenantGuard } from '@coreshop/feature-storefront/guards/storefront-tenant.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('@coreshop/feature-storefront/entry/storefront-entry.component').then(m => m.StorefrontEntryComponent),
    title: 'Tienda',
  },
  {
    path: 'tienda',
    pathMatch: 'full',
    loadComponent: () => import('@coreshop/feature-storefront/entry/storefront-entry.component').then(m => m.StorefrontEntryComponent),
    title: 'Tienda',
  },
  {
    path: 'marketing/unsubscribe',
    loadComponent: () => import('@coreshop/feature-storefront/legal/storefront-unsubscribe.component').then(m => m.StorefrontUnsubscribeComponent),
    title: 'Baja de correos',
  },
  {
    path: 'admin',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@coreshop/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'Inicio - CoreShop',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@coreshop/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'Notificaciones - CoreShop',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@coreshop/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'Configuración - CoreShop',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'Soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@coreshop/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'Confirmar Transferencia de Propiedad - CoreShop',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@coreshop/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'Dashboard - General',
          },
          {
            path: 'comercio',
            loadComponent: () => import('@coreshop/feature-analytics/dashboard/commerce-dashboard.component').then(m => m.CommerceDashboardComponent),
            canActivate: [permissionGuard],
            title: 'Dashboard - Comercio',
          },
        ],
      },
      // Configuración — panel sistema (SUPERADMIN provider, INC_CSH_139)
      {
        path: 'configuracion-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('@coreshop/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
            canActivate: [permissionGuard],
            title: 'Configuración - CoreShop',
          },
          // Pagos vive como pestaña de Configuración (ya no es ítem del menú lateral): los enlaces viejos abren esa pestaña.
          {
            path: 'pagos',
            redirectTo: '/configuracion-sistema?section=pagos',
            pathMatch: 'full',
          },
          {
            path: 'plan',
            loadComponent: () => import('@coreshop/feature-extension/config/plan/config-plan.component').then(m => m.ConfigPlanComponent),
            canActivate: [permissionGuard],
            title: 'Plan',
          },
          {
            path: 'dominios',
            loadComponent: () => import('@coreshop/feature-extension/config/domains/config-domains.component').then(m => m.ConfigDomainsComponent),
            canActivate: [permissionGuard],
            title: 'Dominios',
          },
          {
            path: 'metacampos',
            loadComponent: () => import('@coreshop/feature-extension/config/metafields/config-metafields.component').then(m => m.ConfigMetafieldsComponent),
            canActivate: [permissionGuard],
            title: 'Metacampos',
          },
          {
            path: 'canales-venta',
            loadComponent: () => import('@coreshop/feature-extension/config/channels/config-channels.component').then(m => m.ConfigChannelsComponent),
            canActivate: [permissionGuard],
            title: 'Canales de venta',
          },
          {
            path: 'tasas-impuesto',
            loadComponent: () => import('@coreshop/feature-settings/impuestos/tax-rates.component').then(m => m.TaxRatesComponent),
            canActivate: [permissionGuard],
            title: 'Impuestos',
          },
        ],
      },
      // Informes e-commerce — panel sistema
      {
        path: 'informes-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'ventas', pathMatch: 'full' },
          {
            path: 'ventas',
            loadComponent: () => import('@coreshop/feature-analytics/reports/sales-report.component').then(m => m.SalesReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Ventas',
          },
          {
            path: 'inventario',
            loadComponent: () => import('@coreshop/feature-analytics/reports/inventory-report.component').then(m => m.InventoryReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Inventario',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@coreshop/feature-analytics/reports/customers-report.component').then(m => m.CustomersReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Clientes',
          },
          {
            path: 'financiero',
            loadComponent: () => import('@coreshop/feature-analytics/reports/financial-report.component').then(m => m.FinancialReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Financiero',
          },
          {
            path: 'impuestos',
            loadComponent: () => import('@coreshop/feature-analytics/reports/tax-report.component').then(m => m.TaxReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Impuestos',
          },
          {
            path: 'devoluciones',
            loadComponent: () => import('@coreshop/feature-analytics/reports/returns-report.component').then(m => m.ReturnsReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Devoluciones',
          },
          {
            path: 'geografia',
            loadComponent: () => import('@coreshop/feature-analytics/reports/geo-report.component').then(m => m.GeoReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Geografía',
          },
          {
            path: 'tiempo-real',
            loadComponent: () => import('@coreshop/feature-analytics/live/live-activity.component').then(m => m.LiveActivityComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Tiempo real',
          },
          {
            path: 'eventos',
            loadComponent: () => import('@coreshop/feature-analytics/events/customer-events.component').then(m => m.CustomerEventsComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Eventos del cliente',
          },
        ],
      },
      {
        path: 'marketing-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'segmentos', pathMatch: 'full' },
          {
            path: 'segmentos',
            loadComponent: () => import('@coreshop/feature-marketing/segments/shop-segments-list.component').then(m => m.ShopSegmentsListComponent),
            canActivate: [permissionGuard],
            title: 'Segmentos',
          },
          {
            path: 'campanas',
            loadComponent: () => import('@coreshop/feature-marketing/campaigns/shop-campaigns-list.component').then(m => m.ShopCampaignsListComponent),
            canActivate: [permissionGuard],
            title: 'Campañas',
          },
          {
            path: 'suscriptores',
            loadComponent: () => import('@coreshop/feature-marketing/subscribers/shop-email-subscribers.component').then(m => m.ShopEmailSubscribersComponent),
            canActivate: [permissionGuard],
            title: 'Suscriptores',
            // Sin módulo propio en el menú: usa el permiso de Campañas (igual que el backend).
            data: { permissionUrl: '/marketing-sistema/campanas' },
          },
          {
            path: 'automatizaciones',
            loadComponent: () => import('@coreshop/feature-marketing/automations/shop-automations-list.component').then(m => m.ShopAutomationsListComponent),
            canActivate: [permissionGuard],
            title: 'Automatizaciones',
          },
          {
            path: 'atribucion',
            loadComponent: () => import('@coreshop/feature-marketing/attribution/attribution-report.component').then(m => m.AttributionReportComponent),
            canActivate: [permissionGuard],
            title: 'Atribución',
          },
          {
            path: 'seguimiento',
            loadComponent: () => import('@coreshop/feature-marketing/tracking/tracking-settings.component').then(m => m.TrackingSettingsComponent),
            canActivate: [permissionGuard],
            title: 'Seguimiento',
          },
        ],
      },
      {
        path: 'contenidos-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'archivos', pathMatch: 'full' },
          { path: 'archivos', loadComponent: () => import('@coreshop/feature-content/media/content-media-list.component').then(m => m.ContentMediaListComponent), canActivate: [permissionGuard], title: 'Archivos' },
          { path: 'banners', loadComponent: () => import('@coreshop/feature-content/banners/content-banners-list.component').then(m => m.ContentBannersListComponent), canActivate: [permissionGuard], title: 'Banners' },
          { path: 'menus', loadComponent: () => import('@coreshop/feature-content/menus/content-menus-list.component').then(m => m.ContentMenusListComponent), canActivate: [permissionGuard], title: 'Menús' },
          {
            path: 'blog',
            children: [
              { path: '', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-list.component').then(m => m.ContentBlogListComponent), canActivate: [permissionGuard], title: 'Blog' },
              { path: 'articulos/:id', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-edit.component').then(m => m.ContentBlogEditComponent), canActivate: [permissionGuard], title: 'Editar artículo' },
              { path: 'categorias', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-categories.component').then(m => m.ContentBlogCategoriesComponent), canActivate: [permissionGuard], title: 'Blogs' },
            ],
          },
          { path: 'metaobjetos', loadComponent: () => import('@coreshop/feature-content/metaobjects/content-metaobjects-list.component').then(m => m.ContentMetaobjectsListComponent), canActivate: [permissionGuard], title: 'Metaobjetos' },
        ],
      },
      {
        path: 'canales-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'temas', pathMatch: 'full' },
          { path: 'temas', loadComponent: () => import('@coreshop/feature-content/themes/content-themes-list.component').then(m => m.ContentThemesListComponent), canActivate: [permissionGuard], title: 'Temas' },
          { path: 'paginas', loadComponent: () => import('@coreshop/feature-content/pages/content-pages-list.component').then(m => m.ContentPagesListComponent), canActivate: [permissionGuard], title: 'Páginas' },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@coreshop/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'Usuarios Sistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@coreshop/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'Permisos Sistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@coreshop/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'Permisos Sistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@coreshop/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'Roles Sistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@coreshop/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'Perfil Sistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@coreshop/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'Empresas Sistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@coreshop/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'Detalle de Empresa',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@coreshop/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'Clientes Sistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'Planes Sistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'Detalle de Plan',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'Productos Plataforma',
          },
        ],
      },
      // Catálogo e-commerce — panel sistema (SUPERADMIN provider)
      {
        path: 'catalogo-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'productos',
            pathMatch: 'full',
          },
          {
            path: 'productos',
            loadComponent: () => import('@coreshop/feature-catalog/products/shop-products.component').then(m => m.ShopProductsComponent),
            canActivate: [permissionGuard],
            title: 'Productos',
          },
          {
            path: 'categorias',
            loadComponent: () => import('@coreshop/feature-catalog/categories/shop-categories.component').then(m => m.ShopCategoriesComponent),
            canActivate: [permissionGuard],
            title: 'Categorías',
          },
          {
            path: 'colecciones',
            loadComponent: () => import('@coreshop/feature-catalog/collections/shop-collections.component').then(m => m.ShopCollectionsComponent),
            canActivate: [permissionGuard],
            title: 'Colecciones',
          },
          {
            path: 'atributos',
            loadComponent: () => import('@coreshop/feature-catalog/attributes/shop-attributes.component').then(m => m.ShopAttributesComponent),
            canActivate: [permissionGuard],
            title: 'Atributos',
          },
          {
            path: 'busqueda',
            loadComponent: () => import('@coreshop/feature-catalog/search/shop-search.component').then(m => m.ShopSearchComponent),
            canActivate: [permissionGuard],
            title: 'Búsqueda',
          },
        ],
      },
      {
        path: 'inventario-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'stock', pathMatch: 'full' },
          {
            path: 'stock',
            loadComponent: () => import('@coreshop/feature-inventory/stock/inventory-stock.component').then(m => m.InventoryStockComponent),
            canActivate: [permissionGuard],
            title: 'Stock',
          },
          {
            path: 'alertas',
            loadComponent: () => import('@coreshop/feature-inventory/alerts/inventory-alerts.component').then(m => m.InventoryAlertsComponent),
            canActivate: [permissionGuard],
            title: 'Alertas',
          },
          {
            path: 'proveedores',
            loadComponent: () => import('@coreshop/feature-inventory/suppliers/inventory-suppliers.component').then(m => m.InventorySuppliersComponent),
            canActivate: [permissionGuard],
            title: 'Proveedores',
          },
          {
            path: 'ubicaciones',
            loadComponent: () => import('@coreshop/feature-inventory/locations/inventory-locations.component').then(m => m.InventoryLocationsComponent),
            canActivate: [permissionGuard],
            title: 'Ubicaciones',
          },
          {
            path: 'transferencias',
            loadComponent: () => import('@coreshop/feature-inventory/transfers/inventory-transfers.component').then(m => m.InventoryTransfersComponent),
            canActivate: [permissionGuard],
            title: 'Transferencias',
          },
          {
            path: 'ordenes-compra',
            loadComponent: () => import('@coreshop/feature-inventory/purchase-orders/inventory-purchase-orders.component').then(m => m.InventoryPurchaseOrdersComponent),
            canActivate: [permissionGuard],
            title: 'Órdenes de compra',
          },
        ],
      },
      {
        path: 'compradores-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-customers/list/shop-customers-list.component').then(m => m.ShopCustomersListComponent),
            canActivate: [permissionGuard],
            title: 'Compradores',
          },
        ],
      },
      {
        path: 'descuentos-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'reglas', pathMatch: 'full' },
          {
            path: 'reglas',
            loadComponent: () => import('@coreshop/feature-discounts/rules/shop-discount-rules.component').then(m => m.ShopDiscountRulesComponent),
            canActivate: [permissionGuard],
            title: 'Reglas de descuento',
          },
          {
            path: 'cupones',
            loadComponent: () => import('@coreshop/feature-discounts/coupons/shop-coupons.component').then(m => m.ShopCouponsComponent),
            canActivate: [permissionGuard],
            title: 'Cupones',
          },
          {
            path: 'tarjetas-regalo',
            loadComponent: () => import('@coreshop/feature-extension/gift-cards/extension-gift-cards.component').then(m => m.ExtensionGiftCardsComponent),
            canActivate: [permissionGuard],
            title: 'Tarjetas regalo',
          },
          {
            path: 'puntos',
            loadComponent: () => import('@coreshop/feature-extension/loyalty/extension-loyalty.component').then(m => m.ExtensionLoyaltyComponent),
            canActivate: [permissionGuard],
            title: 'Puntos',
          },
        ],
      },
      {
        path: 'envios-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'zonas', pathMatch: 'full' },
          {
            path: 'zonas',
            loadComponent: () => import('@coreshop/feature-shipping/zones/shipping-zones.component').then(m => m.ShippingZonesComponent),
            canActivate: [permissionGuard],
            title: 'Zonas de envío',
          },
          {
            path: 'metodos',
            loadComponent: () => import('@coreshop/feature-shipping/methods/shipping-methods.component').then(m => m.ShippingMethodsComponent),
            canActivate: [permissionGuard],
            title: 'Métodos de envío',
          },
          {
            path: 'tarifas',
            loadComponent: () => import('@coreshop/feature-shipping/rates/shipping-rates.component').then(m => m.ShippingRatesComponent),
            canActivate: [permissionGuard],
            title: 'Tarifas de envío',
          },
          {
            path: 'empaques',
            loadComponent: () => import('@coreshop/feature-shipping/packaging/shipping-packaging.component').then(m => m.ShippingPackagingComponent),
            canActivate: [permissionGuard],
            title: 'Empaques',
          },
          {
            path: 'puntos',
            loadComponent: () => import('@coreshop/feature-shipping/delivery-points/shipping-delivery-points.component').then(m => m.ShippingDeliveryPointsComponent),
            canActivate: [permissionGuard],
            title: 'Puntos de entrega',
          },
          {
            path: 'couriers',
            loadComponent: () => import('@coreshop/feature-extension/couriers/extension-couriers.component').then(m => m.ExtensionCouriersComponent),
            canActivate: [permissionGuard],
            title: 'Couriers',
          },
        ],
      },
      {
        path: 'mercados-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'catalogos', pathMatch: 'full' },
          {
            path: 'catalogos',
            loadComponent: () => import('@coreshop/feature-extension/markets/extension-markets.component').then(m => m.ExtensionMarketsComponent),
            canActivate: [permissionGuard],
            title: 'Mercados',
          },
        ],
      },
      {
        path: 'apps-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'marketplace', pathMatch: 'full' },
          {
            path: 'marketplace',
            loadComponent: () => import('@coreshop/feature-extension/apps/extension-apps.component').then(m => m.ExtensionAppsComponent),
            canActivate: [permissionGuard],
            title: 'Apps',
          },
          {
            path: 'notificaciones',
            loadComponent: () => import('@coreshop/feature-extension/notifications/extension-notifications.component').then(m => m.ExtensionNotificationsComponent),
            canActivate: [permissionGuard],
            title: 'Notificaciones',
          },
          {
            path: 'correo',
            loadComponent: () => import('@coreshop/feature-extension/email/extension-email-hub.component').then(m => m.ExtensionEmailHubComponent),
            canActivate: [permissionGuard],
            title: 'Correo',
          },
          {
            path: 'whatsapp',
            loadComponent: () => import('@coreshop/feature-extension/whatsapp/extension-whatsapp-hub.component').then(m => m.ExtensionWhatsappHubComponent),
            canActivate: [permissionGuard],
            title: 'WhatsApp API',
          },
          {
            path: 'chatbot',
            loadComponent: () => import('@coreshop/feature-extension/chatbot/extension-chatbot-hub.component').then(m => m.ExtensionChatbotHubComponent),
            canActivate: [permissionGuard],
            title: 'Chatbot',
          },
          {
            path: 'facturacion',
            loadComponent: () => import('@coreshop/feature-extension/billing/extension-billing.component').then(m => m.ExtensionBillingComponent),
            title: 'Facturación',
          },
          {
            path: 'facturacion/impresora',
            loadComponent: () => import('@coreshop/feature-extension/billing/printing/billing-printing.component').then(m => m.BillingPrintingComponent),
            title: 'titles.billingPrinter',
          },
        ],
      },
      {
        path: 'pedidos-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-orders/list/shop-orders-list.component').then(m => m.ShopOrdersListComponent),
            canActivate: [permissionGuard],
            title: 'Pedidos',
          },
          {
            path: 'borradores',
            loadComponent: () => import('@coreshop/feature-orders/list/shop-orders-list.component').then(m => m.ShopOrdersListComponent),
            canActivate: [permissionGuard],
            title: 'Borradores',
            data: { presetOrderStatus: 'DRAFT' },
          },
          {
            path: 'abandonados',
            loadComponent: () => import('@coreshop/feature-orders/abandoned/shop-abandoned-carts.component').then(m => m.ShopAbandonedCartsComponent),
            canActivate: [permissionGuard],
            title: 'Carritos abandonados',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@coreshop/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Gestión de Sesiones - Panel Sistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@coreshop/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Auditorías - Panel Sistema',
          },
          {
            path: 'politicas',
            loadComponent: () => import('@coreshop/feature-security/password-policy/password-policy.component').then(m => m.PasswordPolicyComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Políticas de Contraseña - Panel Sistema',
          },
          {
            path: 'mfa',
            loadComponent: () => import('@coreshop/feature-security/mfa/mfa-policy.component').then(m => m.MfaPolicyComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Verificación en dos pasos - Panel Sistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@coreshop/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'Dashboard - General',
          },
          {
            path: 'comercio',
            loadComponent: () => import('@coreshop/feature-analytics/dashboard/commerce-dashboard.component').then(m => m.CommerceDashboardComponent),
            canActivate: [permissionGuard],
            title: 'Dashboard - Comercio',
          },
        ],
      },
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@coreshop/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'Usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@coreshop/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'Permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@coreshop/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'Permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@coreshop/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'Roles Administrativos',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@coreshop/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'Perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@coreshop/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'Empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@coreshop/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'Detalle de Empresa',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@coreshop/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'Módulos Sistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@coreshop/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'Monedas Sistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@coreshop/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'Monedas',
          },
        ],
      },
      // Sucursales (RF-047) — módulo independiente además del tab en Empresa
      {
        path: 'sucursales',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-admin/sucursales/sucursales.component').then(m => m.SucursalesComponent),
            canActivate: [permissionGuard],
            title: 'Sucursales',
          },
        ],
      },
      {
        path: 'sucursales-sistema',
        canActivate: [adminGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-admin/sucursales/sucursales.component').then(m => m.SucursalesComponent),
            canActivate: [permissionGuard],
            title: 'Sucursales Sistema',
          },
        ],
      },
      // Catálogo e-commerce
      {
        path: 'catalogo',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'productos',
            pathMatch: 'full',
          },
          {
            path: 'productos',
            loadComponent: () => import('@coreshop/feature-catalog/products/shop-products.component').then(m => m.ShopProductsComponent),
            canActivate: [permissionGuard],
            title: 'Productos',
          },
          {
            path: 'categorias',
            loadComponent: () => import('@coreshop/feature-catalog/categories/shop-categories.component').then(m => m.ShopCategoriesComponent),
            canActivate: [permissionGuard],
            title: 'Categorías',
          },
          {
            path: 'colecciones',
            loadComponent: () => import('@coreshop/feature-catalog/collections/shop-collections.component').then(m => m.ShopCollectionsComponent),
            canActivate: [permissionGuard],
            title: 'Colecciones',
          },
          {
            path: 'atributos',
            loadComponent: () => import('@coreshop/feature-catalog/attributes/shop-attributes.component').then(m => m.ShopAttributesComponent),
            canActivate: [permissionGuard],
            title: 'Atributos',
          },
          {
            path: 'busqueda',
            loadComponent: () => import('@coreshop/feature-catalog/search/shop-search.component').then(m => m.ShopSearchComponent),
            canActivate: [permissionGuard],
            title: 'Búsqueda',
          },
        ],
      },
      {
        path: 'inventario',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'stock', pathMatch: 'full' },
          {
            path: 'stock',
            loadComponent: () => import('@coreshop/feature-inventory/stock/inventory-stock.component').then(m => m.InventoryStockComponent),
            canActivate: [permissionGuard],
            title: 'Stock',
          },
          {
            path: 'alertas',
            loadComponent: () => import('@coreshop/feature-inventory/alerts/inventory-alerts.component').then(m => m.InventoryAlertsComponent),
            canActivate: [permissionGuard],
            title: 'Alertas',
          },
          {
            path: 'proveedores',
            loadComponent: () => import('@coreshop/feature-inventory/suppliers/inventory-suppliers.component').then(m => m.InventorySuppliersComponent),
            canActivate: [permissionGuard],
            title: 'Proveedores',
          },
          {
            path: 'ubicaciones',
            loadComponent: () => import('@coreshop/feature-inventory/locations/inventory-locations.component').then(m => m.InventoryLocationsComponent),
            canActivate: [permissionGuard],
            title: 'Ubicaciones',
          },
          {
            path: 'transferencias',
            loadComponent: () => import('@coreshop/feature-inventory/transfers/inventory-transfers.component').then(m => m.InventoryTransfersComponent),
            canActivate: [permissionGuard],
            title: 'Transferencias',
          },
          {
            path: 'ordenes-compra',
            loadComponent: () => import('@coreshop/feature-inventory/purchase-orders/inventory-purchase-orders.component').then(m => m.InventoryPurchaseOrdersComponent),
            canActivate: [permissionGuard],
            title: 'Órdenes de compra',
          },
        ],
      },
      {
        path: 'compradores',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-customers/list/shop-customers-list.component').then(m => m.ShopCustomersListComponent),
            canActivate: [permissionGuard],
            title: 'Compradores',
          },
        ],
      },
      {
        path: 'descuentos',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'reglas', pathMatch: 'full' },
          {
            path: 'reglas',
            loadComponent: () => import('@coreshop/feature-discounts/rules/shop-discount-rules.component').then(m => m.ShopDiscountRulesComponent),
            canActivate: [permissionGuard],
            title: 'Reglas de descuento',
          },
          {
            path: 'cupones',
            loadComponent: () => import('@coreshop/feature-discounts/coupons/shop-coupons.component').then(m => m.ShopCouponsComponent),
            canActivate: [permissionGuard],
            title: 'Cupones',
          },
          {
            path: 'tarjetas-regalo',
            loadComponent: () => import('@coreshop/feature-extension/gift-cards/extension-gift-cards.component').then(m => m.ExtensionGiftCardsComponent),
            canActivate: [permissionGuard],
            title: 'Tarjetas regalo',
          },
          {
            path: 'puntos',
            loadComponent: () => import('@coreshop/feature-extension/loyalty/extension-loyalty.component').then(m => m.ExtensionLoyaltyComponent),
            canActivate: [permissionGuard],
            title: 'Puntos',
          },
        ],
      },
      // Pagos vive como pestaña de Configuración (ya no es ítem del menú lateral): los enlaces viejos abren esa pestaña.
      {
        path: 'configuracion/pagos',
        redirectTo: '/configuracion?section=pagos',
        pathMatch: 'full',
      },
      {
        path: 'configuracion/facturacion',
        redirectTo: '/apps/facturacion',
        pathMatch: 'full',
      },
      {
        path: 'configuracion/plan',
        loadComponent: () => import('@coreshop/feature-extension/config/plan/config-plan.component').then(m => m.ConfigPlanComponent),
        canActivate: [permissionGuard],
        title: 'Plan',
      },
      {
        path: 'configuracion/tasas-impuesto',
        loadComponent: () => import('@coreshop/feature-settings/impuestos/tax-rates.component').then(m => m.TaxRatesComponent),
        canActivate: [permissionGuard],
        title: 'Impuestos',
      },
      {
        path: 'configuracion/dominios',
        loadComponent: () => import('@coreshop/feature-extension/config/domains/config-domains.component').then(m => m.ConfigDomainsComponent),
        canActivate: [permissionGuard],
        title: 'Dominios',
      },
      {
        path: 'configuracion/metacampos',
        loadComponent: () => import('@coreshop/feature-extension/config/metafields/config-metafields.component').then(m => m.ConfigMetafieldsComponent),
        canActivate: [permissionGuard],
        title: 'Metacampos',
      },
      {
        path: 'configuracion/canales-venta',
        loadComponent: () => import('@coreshop/feature-extension/config/channels/config-channels.component').then(m => m.ConfigChannelsComponent),
        canActivate: [permissionGuard],
        title: 'Canales de venta',
      },
      {
        path: 'envios',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'zonas', pathMatch: 'full' },
          {
            path: 'zonas',
            loadComponent: () => import('@coreshop/feature-shipping/zones/shipping-zones.component').then(m => m.ShippingZonesComponent),
            canActivate: [permissionGuard],
            title: 'Zonas de envío',
          },
          {
            path: 'metodos',
            loadComponent: () => import('@coreshop/feature-shipping/methods/shipping-methods.component').then(m => m.ShippingMethodsComponent),
            canActivate: [permissionGuard],
            title: 'Métodos de envío',
          },
          {
            path: 'tarifas',
            loadComponent: () => import('@coreshop/feature-shipping/rates/shipping-rates.component').then(m => m.ShippingRatesComponent),
            canActivate: [permissionGuard],
            title: 'Tarifas de envío',
          },
          {
            path: 'empaques',
            loadComponent: () => import('@coreshop/feature-shipping/packaging/shipping-packaging.component').then(m => m.ShippingPackagingComponent),
            canActivate: [permissionGuard],
            title: 'Empaques',
          },
          {
            path: 'puntos',
            loadComponent: () => import('@coreshop/feature-shipping/delivery-points/shipping-delivery-points.component').then(m => m.ShippingDeliveryPointsComponent),
            canActivate: [permissionGuard],
            title: 'Puntos de entrega',
          },
          {
            path: 'couriers',
            loadComponent: () => import('@coreshop/feature-extension/couriers/extension-couriers.component').then(m => m.ExtensionCouriersComponent),
            canActivate: [permissionGuard],
            title: 'Couriers',
          },
        ],
      },
      {
        path: 'mercados',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'catalogos', pathMatch: 'full' },
          {
            path: 'catalogos',
            loadComponent: () => import('@coreshop/feature-extension/markets/extension-markets.component').then(m => m.ExtensionMarketsComponent),
            canActivate: [permissionGuard],
            title: 'Mercados',
          },
        ],
      },
      {
        path: 'apps',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'marketplace', pathMatch: 'full' },
          {
            path: 'marketplace',
            loadComponent: () => import('@coreshop/feature-extension/apps/extension-apps.component').then(m => m.ExtensionAppsComponent),
            canActivate: [permissionGuard],
            title: 'Apps',
          },
          {
            path: 'notificaciones',
            loadComponent: () => import('@coreshop/feature-extension/notifications/extension-notifications.component').then(m => m.ExtensionNotificationsComponent),
            canActivate: [permissionGuard],
            title: 'Notificaciones',
          },
          {
            path: 'correo',
            loadComponent: () => import('@coreshop/feature-extension/email/extension-email-hub.component').then(m => m.ExtensionEmailHubComponent),
            canActivate: [permissionGuard],
            title: 'Correo',
          },
          {
            path: 'whatsapp',
            loadComponent: () => import('@coreshop/feature-extension/whatsapp/extension-whatsapp-hub.component').then(m => m.ExtensionWhatsappHubComponent),
            canActivate: [permissionGuard],
            title: 'WhatsApp API',
          },
          {
            path: 'chatbot',
            loadComponent: () => import('@coreshop/feature-extension/chatbot/extension-chatbot-hub.component').then(m => m.ExtensionChatbotHubComponent),
            canActivate: [permissionGuard],
            title: 'Chatbot',
          },
          {
            path: 'facturacion',
            loadComponent: () => import('@coreshop/feature-extension/billing/extension-billing.component').then(m => m.ExtensionBillingComponent),
            title: 'Facturación',
          },
          {
            path: 'facturacion/impresora',
            loadComponent: () => import('@coreshop/feature-extension/billing/printing/billing-printing.component').then(m => m.BillingPrintingComponent),
            title: 'titles.billingPrinter',
          },
        ],
      },
      {
        path: 'pedidos',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'listado', pathMatch: 'full' },
          {
            path: 'listado',
            loadComponent: () => import('@coreshop/feature-orders/list/shop-orders-list.component').then(m => m.ShopOrdersListComponent),
            canActivate: [permissionGuard],
            title: 'Pedidos',
          },
          {
            path: 'borradores',
            loadComponent: () => import('@coreshop/feature-orders/list/shop-orders-list.component').then(m => m.ShopOrdersListComponent),
            canActivate: [permissionGuard],
            title: 'Borradores',
            data: { presetOrderStatus: 'DRAFT' },
          },
          {
            path: 'abandonados',
            loadComponent: () => import('@coreshop/feature-orders/abandoned/shop-abandoned-carts.component').then(m => m.ShopAbandonedCartsComponent),
            canActivate: [permissionGuard],
            title: 'Carritos abandonados',
          },
        ],
      },
      {
        path: 'informes',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'ventas', pathMatch: 'full' },
          {
            path: 'ventas',
            loadComponent: () => import('@coreshop/feature-analytics/reports/sales-report.component').then(m => m.SalesReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Ventas',
          },
          {
            path: 'inventario',
            loadComponent: () => import('@coreshop/feature-analytics/reports/inventory-report.component').then(m => m.InventoryReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Inventario',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@coreshop/feature-analytics/reports/customers-report.component').then(m => m.CustomersReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Clientes',
          },
          {
            path: 'financiero',
            loadComponent: () => import('@coreshop/feature-analytics/reports/financial-report.component').then(m => m.FinancialReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Financiero',
          },
          {
            path: 'impuestos',
            loadComponent: () => import('@coreshop/feature-analytics/reports/tax-report.component').then(m => m.TaxReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Impuestos',
          },
          {
            path: 'devoluciones',
            loadComponent: () => import('@coreshop/feature-analytics/reports/returns-report.component').then(m => m.ReturnsReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Devoluciones',
          },
          {
            path: 'geografia',
            loadComponent: () => import('@coreshop/feature-analytics/reports/geo-report.component').then(m => m.GeoReportComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Geografía',
          },
          {
            path: 'tiempo-real',
            loadComponent: () => import('@coreshop/feature-analytics/live/live-activity.component').then(m => m.LiveActivityComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Tiempo real',
          },
          {
            path: 'eventos',
            loadComponent: () => import('@coreshop/feature-analytics/events/customer-events.component').then(m => m.CustomerEventsComponent),
            canActivate: [permissionGuard],
            title: 'Informes - Eventos del cliente',
          },
        ],
      },
      {
        path: 'marketing',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'segmentos', pathMatch: 'full' },
          {
            path: 'segmentos',
            loadComponent: () => import('@coreshop/feature-marketing/segments/shop-segments-list.component').then(m => m.ShopSegmentsListComponent),
            canActivate: [permissionGuard],
            title: 'Segmentos',
          },
          {
            path: 'campanas',
            loadComponent: () => import('@coreshop/feature-marketing/campaigns/shop-campaigns-list.component').then(m => m.ShopCampaignsListComponent),
            canActivate: [permissionGuard],
            title: 'Campañas',
          },
          {
            path: 'suscriptores',
            loadComponent: () => import('@coreshop/feature-marketing/subscribers/shop-email-subscribers.component').then(m => m.ShopEmailSubscribersComponent),
            canActivate: [permissionGuard],
            title: 'Suscriptores',
            // Sin módulo propio en el menú: usa el permiso de Campañas (igual que el backend).
            data: { permissionUrl: '/marketing/campanas' },
          },
          {
            path: 'automatizaciones',
            loadComponent: () => import('@coreshop/feature-marketing/automations/shop-automations-list.component').then(m => m.ShopAutomationsListComponent),
            canActivate: [permissionGuard],
            title: 'Automatizaciones',
          },
          {
            path: 'atribucion',
            loadComponent: () => import('@coreshop/feature-marketing/attribution/attribution-report.component').then(m => m.AttributionReportComponent),
            canActivate: [permissionGuard],
            title: 'Atribución',
          },
          {
            path: 'seguimiento',
            loadComponent: () => import('@coreshop/feature-marketing/tracking/tracking-settings.component').then(m => m.TrackingSettingsComponent),
            canActivate: [permissionGuard],
            title: 'Seguimiento',
          },
        ],
      },
      {
        path: 'contenidos',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'archivos', pathMatch: 'full' },
          { path: 'archivos', loadComponent: () => import('@coreshop/feature-content/media/content-media-list.component').then(m => m.ContentMediaListComponent), canActivate: [permissionGuard], title: 'Archivos' },
          { path: 'banners', loadComponent: () => import('@coreshop/feature-content/banners/content-banners-list.component').then(m => m.ContentBannersListComponent), canActivate: [permissionGuard], title: 'Banners' },
          { path: 'menus', loadComponent: () => import('@coreshop/feature-content/menus/content-menus-list.component').then(m => m.ContentMenusListComponent), canActivate: [permissionGuard], title: 'Menús' },
          {
            path: 'blog',
            children: [
              { path: '', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-list.component').then(m => m.ContentBlogListComponent), canActivate: [permissionGuard], title: 'Blog' },
              { path: 'articulos/:id', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-edit.component').then(m => m.ContentBlogEditComponent), canActivate: [permissionGuard], title: 'Editar artículo' },
              { path: 'categorias', loadComponent: () => import('@coreshop/feature-content/blog/content-blog-categories.component').then(m => m.ContentBlogCategoriesComponent), canActivate: [permissionGuard], title: 'Blogs' },
            ],
          },
          { path: 'metaobjetos', loadComponent: () => import('@coreshop/feature-content/metaobjects/content-metaobjects-list.component').then(m => m.ContentMetaobjectsListComponent), canActivate: [permissionGuard], title: 'Metaobjetos' },
        ],
      },
      {
        path: 'canales',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'temas', pathMatch: 'full' },
          { path: 'temas', loadComponent: () => import('@coreshop/feature-content/themes/content-themes-list.component').then(m => m.ContentThemesListComponent), canActivate: [permissionGuard], title: 'Temas' },
          { path: 'paginas', loadComponent: () => import('@coreshop/feature-content/pages/content-pages-list.component').then(m => m.ContentPagesListComponent), canActivate: [permissionGuard], title: 'Páginas' },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@coreshop/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Gestión de Sesiones - Panel de Control',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@coreshop/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Auditorías - Panel de Auditoría',
          },
          {
            path: 'politicas',
            loadComponent: () => import('@coreshop/feature-security/password-policy/password-policy.component').then(m => m.PasswordPolicyComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Políticas - Panel de Control',
          },
          {
            path: 'mfa',
            loadComponent: () => import('@coreshop/feature-security/mfa/mfa-policy.component').then(m => m.MfaPolicyComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Verificación en dos pasos - Panel de Control',
          },
        ],
      },
    ],
  },
  // Storefront público (sin auth admin)
  {
    path: 'store/:storeSlug',
    canActivate: [storefrontTenantGuard],
    loadComponent: () => import('@coreshop/feature-storefront/layout/storefront-layout.component').then(m => m.StorefrontLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@coreshop/feature-storefront/home/storefront-home.component').then(m => m.StorefrontHomeComponent),
        title: 'title.home',
      },
      {
        path: 'tienda',
        loadComponent: () => import('@coreshop/feature-storefront/catalog/storefront-catalog.component').then(m => m.StorefrontCatalogComponent),
        title: 'title.catalog',
      },
      {
        path: 'product/:slug',
        loadComponent: () => import('@coreshop/feature-storefront/product/storefront-product.component').then(m => m.StorefrontProductComponent),
        title: 'title.product',
      },
      {
        path: 'cart',
        loadComponent: () => import('@coreshop/feature-storefront/cart/storefront-cart.component').then(m => m.StorefrontCartComponent),
        title: 'title.cart',
      },
      {
        path: 'checkout',
        loadComponent: () => import('@coreshop/feature-storefront/checkout/storefront-checkout.component').then(m => m.StorefrontCheckoutComponent),
        title: 'title.checkout',
      },
      {
        path: 'blog',
        loadComponent: () => import('@coreshop/feature-storefront/blog/storefront-blog.component').then(m => m.StorefrontBlogComponent),
        title: 'title.blog',
      },
      {
        path: 'blogs/:categorySlug/tagged/:tagSlug',
        loadComponent: () => import('@coreshop/feature-storefront/blog/storefront-blog.component').then(m => m.StorefrontBlogComponent),
        title: 'title.blogTag',
      },
      {
        path: 'blog/preview/:id',
        loadComponent: () => import('@coreshop/feature-storefront/blog/storefront-blog-post.component').then(m => m.StorefrontBlogPostComponent),
        title: 'title.preview',
      },
      {
        path: 'blog/:slug',
        loadComponent: () => import('@coreshop/feature-storefront/blog/storefront-blog-post.component').then(m => m.StorefrontBlogPostComponent),
        title: 'title.article',
      },
      {
        path: 'pages/:slug',
        loadComponent: () => import('@coreshop/feature-storefront/pages/storefront-page.component').then(m => m.StorefrontPageComponent),
        title: 'title.page',
      },
      {
        path: 'collections',
        loadComponent: () => import('@coreshop/feature-storefront/collections/storefront-collections.component').then(m => m.StorefrontCollectionsComponent),
        title: 'title.collections',
      },
      {
        path: 'collections/:slug/products',
        loadComponent: () => import('@coreshop/feature-storefront/collection-products/storefront-collection-products.component').then(m => m.StorefrontCollectionProductsComponent),
        title: 'title.collection',
      },
      {
        path: 'collections/:slug',
        loadComponent: () => import('@coreshop/feature-storefront/collection-products/storefront-collection-products.component').then(m => m.StorefrontCollectionProductsComponent),
        title: 'title.collection',
      },
      {
        path: 'login',
        loadComponent: () => import('@coreshop/feature-storefront/auth/storefront-login.component').then(m => m.StorefrontLoginComponent),
        title: 'title.login',
      },
      {
        path: 'register',
        loadComponent: () => import('@coreshop/feature-storefront/auth/storefront-register.component').then(m => m.StorefrontRegisterComponent),
        title: 'title.register',
      },
      {
        path: 'verify-email',
        loadComponent: () => import('@coreshop/feature-storefront/auth/storefront-verify-email.component').then(m => m.StorefrontVerifyEmailComponent),
        title: 'title.verifyEmail',
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@coreshop/feature-storefront/auth/storefront-forgot-password.component').then(m => m.StorefrontForgotPasswordComponent),
        title: 'title.forgotPassword',
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@coreshop/feature-storefront/auth/storefront-reset-password.component').then(m => m.StorefrontResetPasswordComponent),
        title: 'title.resetPassword',
      },
      {
        path: 'account',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-account.component').then(m => m.StorefrontAccountComponent),
        title: 'title.account',
      },
      {
        path: 'account/orders',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-orders.component').then(m => m.StorefrontOrdersComponent),
        title: 'title.orders',
      },
      {
        path: 'account/orders/:orderId',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-order-detail.component').then(m => m.StorefrontOrderDetailComponent),
        title: 'title.orderDetail',
      },
      {
        path: 'account/profile',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-profile.component').then(m => m.StorefrontProfileComponent),
        title: 'title.profile',
      },
      {
        path: 'account/addresses',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-addresses.component').then(m => m.StorefrontAddressesComponent),
        title: 'title.addresses',
      },
      {
        path: 'account/wishlist',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-wishlist.component').then(m => m.StorefrontWishlistComponent),
        title: 'title.wishlist',
      },
      {
        path: 'account/notifications',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-notifications.component').then(m => m.StorefrontNotificationsComponent),
        title: 'title.notifications',
      },
      {
        path: 'account/loyalty',
        canActivate: [storefrontCustomerGuard],
        loadComponent: () => import('@coreshop/feature-storefront/account/storefront-loyalty.component').then(m => m.StorefrontLoyaltyComponent),
        title: 'title.loyalty',
      },
      {
        path: 'faq',
        loadComponent: () => import('@coreshop/feature-storefront/faq/storefront-faq.component').then(m => m.StorefrontFaqComponent),
        title: 'title.faq',
      },
      {
        path: 'legal/:doc',
        loadComponent: () => import('@coreshop/feature-storefront/legal/storefront-legal.component').then(m => m.StorefrontLegalComponent),
        title: 'title.legal',
      },
      {
        path: 'quote/:token',
        loadComponent: () => import('@coreshop/feature-storefront/quote/storefront-quote.component').then(m => m.StorefrontQuoteComponent),
        title: 'title.quote',
      },
      {
        path: 'gift-card/balance',
        loadComponent: () => import('@coreshop/feature-storefront/gift-card/storefront-gift-card-balance.component').then(m => m.StorefrontGiftCardBalanceComponent),
        title: 'title.giftCard',
      },
    ],
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@coreshop/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@coreshop/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@coreshop/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@coreshop/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@coreshop/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'Página no encontrada - CoreShop',
  },
  {
    path: '**',
    loadComponent: () => import('@coreshop/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'Página no encontrada - CoreShop',
  },
];

